import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "../ui/button";
import { AREA_CATALOG, ALL_UNITS_VALUE, UNIT_SCOPED_KEYS } from "./assignmentConstants";

const selectCls = "rounded-lg border border-border px-2 py-1.5 text-sm bg-white flex-1";

// Repeatable area rows — each area_type may be added more than once (e.g. two different
// pods) so an officer/nurse can cover more than one unit/area at a time.
export function AssignmentAreaPicker({ areas, setAreas, units, allowedKeys, testIdPrefix = "assign-area" }) {
    const catalog = allowedKeys ? AREA_CATALOG.filter((a) => allowedKeys.includes(a.key)) : AREA_CATALOG;

    const updateRow = (idx, patch) => {
        setAreas(areas.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
    };
    const addRow = () => {
        const first = catalog[0];
        setAreas([...areas, { area_type: first.key, area_value: first.unitScoped ? "" : null }]);
    };
    const removeRow = (idx) => setAreas(areas.filter((_, i) => i !== idx));

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-list`}>
            {areas.map((row, idx) => {
                const meta = AREA_CATALOG.find((a) => a.key === row.area_type);
                return (
                    <div key={idx} className="flex items-center gap-2">
                        <select data-testid={`${testIdPrefix}-type-${idx}`} className={selectCls} value={row.area_type}
                            onChange={(e) => {
                                const m = AREA_CATALOG.find((a) => a.key === e.target.value);
                                updateRow(idx, { area_type: e.target.value, area_value: m?.unitScoped ? "" : null });
                            }}>
                            {catalog.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                        </select>
                        {meta?.unitScoped && (
                            <select data-testid={`${testIdPrefix}-unit-${idx}`} className={selectCls} value={row.area_value || ""}
                                onChange={(e) => updateRow(idx, { area_value: e.target.value })}>
                                <option value="">— pick a unit —</option>
                                <option value={ALL_UNITS_VALUE}>All units</option>
                                {units.map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                        )}
                        {areas.length > 1 && (
                            <button type="button" data-testid={`${testIdPrefix}-remove-${idx}`} onClick={() => removeRow(idx)}
                                className="p-1.5 text-muted-foreground hover:text-destructive">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                );
            })}
            <Button type="button" variant="outline" size="sm" data-testid={`${testIdPrefix}-add`} onClick={addRow} className="gap-1.5">
                <Plus className="w-3.5 h-3.5" /> Add another area
            </Button>
        </div>
    );
}

// Simple start/end date pair with the 4-week (28 day) v1 planning cap.
export function DateRangeFields({ startDate, setStartDate, endDate, setEndDate, maxSpanDays, testIdPrefix = "assign" }) {
    const [spanError, setSpanError] = useState("");
    const onEnd = (val) => {
        setEndDate(val);
        if (startDate && val) {
            const days = (new Date(val) - new Date(startDate)) / 86400000;
            setSpanError(days > maxSpanDays - 1 ? `An assignment can span at most ${maxSpanDays} days at a time` : "");
        }
    };
    return (
        <div>
            <div className="flex items-center gap-2">
                <input type="date" data-testid={`${testIdPrefix}-start-date`} className={selectCls} value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); if (endDate && e.target.value > endDate) setEndDate(e.target.value); }} />
                <span className="text-xs text-muted-foreground">to</span>
                <input type="date" data-testid={`${testIdPrefix}-end-date`} className={selectCls} value={endDate} min={startDate}
                    onChange={(e) => onEnd(e.target.value)} />
            </div>
            {spanError && <p className="text-[11px] text-destructive mt-1">{spanError}</p>}
        </div>
    );
}
